import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import "./style.css";
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { openModal } from '../../../../redux/reducers/global/modal/modalSlice';
import { selectSelectedDiagram } from '../../../../redux/reducers/global/diagram/diagramSlice';
import ExportToExcelButton from '../../../global/shared/ExportToExcelButton';
import DownloadButton from '../../../global/shared/downloadbutton';
import DownloadButtonPDF from '../../../global/shared/btnexporpdf';
import ExportToDXFButton from '../../../global/shared/ExportToDXFButton';
import NavbarLogo from '../../../../assets/ico-SL-NavBar.svg';
import { performRedo, performUndo } from '../../../../redux/reducers/global/history/historySlice';
import { getPrimaryModifierKey, formatShortcut } from '../../../../utilities/platform';
import { MODAL_TYPES } from '../../../global/modal/modalTypes';
import { registerModalTask } from '../../../global/modal/modalTaskRegistry';

export const Navbar = ({
    onSave,
    onToggleInspector,
    isInspectorOpen,
    onSelectAll,
    onCloneSelection,
    editorTemplates = [],
    templatesLoading = false,
    onInsertTemplate,
    onDeleteTemplate,
}) => {
    const selectedDiagram = useSelector(selectSelectedDiagram);
    const [hamburgerOpen, setHamburgerOpen] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [templateToDelete, setTemplateToDelete] = useState(null);
    const [isDeletingTemplate, setDeletingTemplate] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const navRef = useRef(null);

    const closeDropdowns = useCallback(() => {
        setActiveDropdown(null);
    }, []);

    const toggleDropdown = useCallback((key) => {
        setHamburgerOpen(false);
        setActiveDropdown((prev) => (prev === key ? null : key));
    }, []);

    useEffect(() => {
        const handleClickOutside = (evt) => {
            if (!navRef.current) return;
            if (!navRef.current.contains(evt.target)) {
                closeDropdowns();
                setHamburgerOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [closeDropdowns]);

    useEffect(() => {
        const handleEscape = (evt) => {
            if (evt.key === 'Escape') {
                setTemplateToDelete(null);
                closeDropdowns();
                setHamburgerOpen(false);
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [closeDropdowns]);

    const handleSave = useCallback(() => {
        closeDropdowns();
        if (typeof onSave === 'function') {
            onSave();
        }
    }, [onSave, closeDropdowns]);

    const handleNewDiagram = useCallback(() => {
        closeDropdowns();
        dispatch(openModal({ modalType: MODAL_TYPES.NEW_DIAGRAM, modalProps: {} }));
    }, [dispatch, closeDropdowns]);

    const handleOpenAnother = useCallback(() => {
        closeDropdowns();
        window.open('/', '_blank', 'noopener,noreferrer');
    }, [closeDropdowns]);

    const handleReturnToProjects = useCallback(() => {
        closeDropdowns();
        setHamburgerOpen(false);
        const taskId = registerModalTask(async (action) => {
            if (action === 'save') {
                if (typeof onSave === 'function') {
                    const result = await onSave();
                    if (result === false) {
                        return false;
                    }
                }
                navigate('/');
                return true;
            }
            if (action === 'exit') {
                navigate('/');
                return true;
            }
            return false;
        });
        dispatch(openModal({
            modalType: MODAL_TYPES.CONFIRM_EXIT_PROJECTS,
            modalProps: {
                taskId,
            }
        }));
    }, [dispatch, closeDropdowns, navigate, onSave]);

    const handleShortcuts = useCallback(() => {
        closeDropdowns();
        setHamburgerOpen(false);
        dispatch(openModal({ modalType: MODAL_TYPES.EDITOR_SHORTCUTS, modalProps: {} }));
    }, [dispatch, closeDropdowns]);

    const handleUndo = useCallback(() => {
        closeDropdowns();
        dispatch(performUndo());
    }, [dispatch, closeDropdowns]);

    const handleRedo = useCallback(() => {
        closeDropdowns();
        dispatch(performRedo());
    }, [dispatch, closeDropdowns]);

    const handleToggleInspector = useCallback(() => {
        closeDropdowns();
        setHamburgerOpen(false);
        if (typeof onToggleInspector === 'function') {
            onToggleInspector();
        }
    }, [closeDropdowns, onToggleInspector]);

    const handleSelectAll = useCallback(() => {
        closeDropdowns();
        setHamburgerOpen(false);
        if (typeof onSelectAll === 'function') {
            onSelectAll();
        }
    }, [closeDropdowns, onSelectAll]);

    const handleClone = useCallback(() => {
        closeDropdowns();
        setHamburgerOpen(false);
        if (typeof onCloneSelection === 'function') {
            onCloneSelection();
        }
    }, [closeDropdowns, onCloneSelection]);

    const handleInsertTemplate = useCallback((template) => {
        closeDropdowns();
        setHamburgerOpen(false);
        if (typeof onInsertTemplate === 'function') {
            onInsertTemplate(template);
        }
    }, [closeDropdowns, onInsertTemplate]);

    const handleRequestDeleteTemplate = useCallback((event, template) => {
        event.preventDefault();
        event.stopPropagation();
        closeDropdowns();
        setTemplateToDelete(template);
        setHamburgerOpen(false);
    }, [closeDropdowns]);

    const handleCancelDeleteTemplate = useCallback(() => {
        if (isDeletingTemplate) return;
        setTemplateToDelete(null);
    }, [isDeletingTemplate]);

    const handleConfirmDeleteTemplate = useCallback(async () => {
        if (!templateToDelete || typeof onDeleteTemplate !== 'function') {
            setTemplateToDelete(null);
            return;
        }
        setDeletingTemplate(true);
        const deleted = await onDeleteTemplate(templateToDelete);
        setDeletingTemplate(false);
        if (deleted !== false) {
            setTemplateToDelete(null);
        }
    }, [onDeleteTemplate, templateToDelete]);

    const handleOpenSettings = useCallback(() => {
        closeDropdowns();
        setHamburgerOpen(false);
        dispatch(openModal({ modalType: MODAL_TYPES.EDITOR_SETTINGS, modalProps: {} }));
    }, [dispatch, closeDropdowns]);

    const toggleHamburger = useCallback(() => {
        closeDropdowns();
        setHamburgerOpen((prev) => !prev);
    }, [closeDropdowns]);

    const diagramLabel = selectedDiagram?.label || '';
    const inspectorToggleLabel = isInspectorOpen ? 'Ocultar inspector' : 'Desplegar inspector';
    const primaryModifierKey = useMemo(() => getPrimaryModifierKey(), []);
    const saveShortcutLabel = useMemo(
        () => formatShortcut(primaryModifierKey, 'G'),
        [primaryModifierKey]
    );
    const undoShortcutLabel = useMemo(
        () => formatShortcut(primaryModifierKey, 'Z'),
        [primaryModifierKey]
    );
    const redoShortcutLabel = useMemo(
        () => formatShortcut(primaryModifierKey, 'Shift', 'Z'),
        [primaryModifierKey]
    );
    const selectAllShortcutLabel = useMemo(
        () => formatShortcut(primaryModifierKey, 'A'),
        [primaryModifierKey]
    );
    const cloneShortcutLabel = useMemo(
        () => formatShortcut(primaryModifierKey, 'C'),
        [primaryModifierKey]
    );
 
    const isDropdownOpen = (key) => activeDropdown === key;

    return (
        <nav className="top-nav" ref={navRef}>
            <div className="nav-left">
                <h2 className="nav-title">
                    <button
                        type="button"
                        className="nav-title-home-btn"
                        onClick={handleReturnToProjects}
                        aria-label="Volver al panel de proyectos"
                    >
                        <img src={NavbarLogo} alt="Solo Line" className="nav-title-icon" />
                    </button>
                    {diagramLabel ? (
                        <>
                            <span className="nav-title-label">{diagramLabel}</span>
                        </>
                    ) : null}
                </h2>
                <div className="nav-divider" aria-hidden="true" />
                <div className="nav-links">
                    <div className={`nav-dropdown ${isDropdownOpen('archivo') ? 'open' : ''}`}>
                        <button
                            type="button"
                            className="nav-link"
                            onClick={() => toggleDropdown('archivo')}
                            aria-haspopup="true"
                            aria-expanded={isDropdownOpen('archivo')}
                        >
                            Archivo
                        </button>
                        <div className="nav-dropdown-menu" role="menu">
                            <button type="button" className="nav-dropdown-item nav-dropdown-item--shortcut" onClick={handleSave}>
                                <span>Guardar</span>
                                <span className="nav-dropdown-item-shortcut">{saveShortcutLabel}</span>
                            </button>
                            <button type="button" className="nav-dropdown-item" onClick={handleNewDiagram}>
                                Nuevo archivo
                            </button>
                            <button type="button" className="nav-dropdown-item" onClick={handleReturnToProjects}>
                                Panel de proyectos
                            </button>
                        </div>
                    </div>
                    <div className={`nav-dropdown ${isDropdownOpen('editar') ? 'open' : ''}`}>
                        <button
                            type="button"
                            className="nav-link"
                            onClick={() => toggleDropdown('editar')}
                            aria-haspopup="true"
                            aria-expanded={isDropdownOpen('editar')}
                        >
                            Editar
                        </button>
                        <div className="nav-dropdown-menu" role="menu">
                            <button type="button" className="nav-dropdown-item nav-dropdown-item--shortcut" onClick={handleUndo}>
                                <span>Deshacer</span>
                                <span className="nav-dropdown-item-shortcut">{undoShortcutLabel}</span>
                            </button>
                            <button type="button" className="nav-dropdown-item nav-dropdown-item--shortcut" onClick={handleRedo}>
                                <span>Rehacer</span>
                                <span className="nav-dropdown-item-shortcut">{redoShortcutLabel}</span>
                            </button>
                            <button type="button" className="nav-dropdown-item nav-dropdown-item--shortcut" onClick={handleClone}>
                                <span>Clonar</span>
                                <span className="nav-dropdown-item-shortcut">{cloneShortcutLabel}</span>
                            </button>
                            <button type="button" className="nav-dropdown-item nav-dropdown-item--shortcut" onClick={handleSelectAll}>
                                <span>Seleccionar todo</span>
                                <span className="nav-dropdown-item-shortcut">{selectAllShortcutLabel}</span>
                            </button>
                            <button type="button" className="nav-dropdown-item nav-dropdown-item--shortcut" onClick={handleToggleInspector}>
                                <span>{inspectorToggleLabel}</span>
                                <span className="nav-dropdown-item-shortcut">I</span>
                            </button>
                        </div>
                    </div>
                    <div className={`nav-dropdown ${isDropdownOpen('exportar') ? 'open' : ''}`}>
                        <button
                            type="button"
                            className="nav-link"
                            onClick={() => toggleDropdown('exportar')}
                            aria-haspopup="true"
                            aria-expanded={isDropdownOpen('exportar')}
                        >
                            Exportar
                        </button>
                        <div className="nav-dropdown-menu" role="menu">
                            <div className="nav-dropdown-section">Reportes</div>
                            <div className="nav-dropdown-action" onClick={closeDropdowns}>
                                <ExportToExcelButton>
                                    <span className="nav-dropdown-link">Excel</span>
                                </ExportToExcelButton>
                            </div>
                            <div className="nav-dropdown-section">Diagramas</div>
                            <div className="nav-dropdown-action" onClick={closeDropdowns}>
                                <DownloadButton>
                                    <span className="nav-dropdown-link">PNG</span>
                                </DownloadButton>
                            </div>
                            <div className="nav-dropdown-action" onClick={closeDropdowns}>
                                <DownloadButtonPDF>
                                    <span className="nav-dropdown-link">PDF</span>
                                </DownloadButtonPDF>
                            </div>
                            <div className="nav-dropdown-action" onClick={closeDropdowns}>
                                <ExportToDXFButton>
                                    <span className="nav-dropdown-link">DXF</span>
                                </ExportToDXFButton>
                            </div>
                        </div>
                    </div>
                    <div className={`nav-dropdown ${isDropdownOpen('plantillas') ? 'open' : ''}`}>
                        <button
                            type="button"
                            className="nav-link"
                            onClick={() => toggleDropdown('plantillas')}
                            aria-haspopup="true"
                            aria-expanded={isDropdownOpen('plantillas')}
                        >
                            Plantillas
                        </button>
                        <div className="nav-dropdown-menu nav-dropdown-menu--templates" role="menu">
                            {templatesLoading ? (
                                <div className="nav-dropdown-empty">Cargando plantillas...</div>
                            ) : Array.isArray(editorTemplates) && editorTemplates.length > 0 ? (
                                editorTemplates.map((template) => (
                                    <div
                                        key={template.uuid || template.id}
                                        className="nav-dropdown-template-row"
                                    >
                                        <button
                                            type="button"
                                            className="nav-dropdown-item nav-dropdown-item--template"
                                            onClick={() => handleInsertTemplate(template)}
                                        >
                                            {template.label || 'Plantilla sin nombre'}
                                        </button>
                                        <button
                                            type="button"
                                            className="nav-template-delete-button"
                                            onClick={(event) => handleRequestDeleteTemplate(event, template)}
                                            aria-label={`Eliminar ${template.label || 'plantilla sin nombre'}`}
                                            title="Eliminar plantilla"
                                        >
                                            <svg
                                                width="18"
                                                height="18"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                                aria-hidden="true"
                                            >
                                                <path d="M6 7h12l-1 12H7L6 7z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M4 7h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                                <path d="M9 7V4h6v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M10 11v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                                <path d="M14 11v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                            </svg>
                                        </button>
                                    </div>
                                ))
                            ) : (
                                <div className="nav-dropdown-empty">No se han agregado plantillas</div>
                            )}
                        </div>
                    </div>
      
                    <button type="button" className="nav-link" onClick={handleShortcuts}>
                        Atajos
                    </button>
                </div>
            </div>
            <div className="nav-menu">
                <button
                    className={`hamburger ${hamburgerOpen ? 'is-open' : ''}`}
                    onClick={toggleHamburger}
                    aria-label="Abrir menú"
                    aria-expanded={hamburgerOpen}
                >
                    <span />
                    <span />
                    <span />
                </button>
                <div className={`dropdown ${hamburgerOpen ? 'open' : ''}`} onClick={() => setHamburgerOpen(false)}>
                    <button type="button" className="dropdown-item" onClick={handleReturnToProjects}>
                        Panel de proyectos
                    </button>
                    <a href="" className="dropdown-item">Suscripciones</a>
                    <button type="button" className="dropdown-item" onClick={handleOpenSettings}>
                        Configuración
                    </button>
                    <a href="" className="dropdown-item">Cerrar sesión</a>
                </div>
            </div>
            {templateToDelete ? (
                <div className="template-delete-modal-backdrop" role="presentation">
                    <div
                        className="template-delete-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="template-delete-modal-title"
                    >
                        <h3 id="template-delete-modal-title">Eliminar plantilla</h3>
                        <p>
                            ¿Deseas borrar la plantilla <strong>{templateToDelete.label || 'Plantilla sin nombre'}</strong> definitivamente?
                        </p>
                        <div className="template-delete-modal-actions">
                            <button
                                type="button"
                                className="template-delete-modal-button is-secondary"
                                onClick={handleCancelDeleteTemplate}
                                disabled={isDeletingTemplate}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="template-delete-modal-button is-danger"
                                onClick={handleConfirmDeleteTemplate}
                                disabled={isDeletingTemplate}
                            >
                                {isDeletingTemplate ? 'Eliminando...' : 'Eliminar'}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </nav>
    );
};
